import { useAutoneTranslation } from '@autone/translations';
import {
  forwardRef,
  type HTMLAttributes,
  type MouseEvent,
  type PropsWithChildren,
  type PropsWithRef,
  type ReactNode,
  type RefAttributes,
  useEffect,
  useImperativeHandle,
  useReducer,
  useRef,
} from 'react';

import { Button, type ButtonProps } from '../../../atoms';
import { PlusIcon } from '../../../icons';
import { cn } from '../../../utils';

import {
  AdvancedFiltersContext,
  useAdvancedFiltersContext,
} from './AdvancedFilters.context';
import { advancedFiltersReducer } from './AdvancedFilters.reducer';
import { ClearAllButton } from './components/AdvancedFiltersClearAllButton';
import { AdvancedFiltersConditionCombineMode } from './components/AdvancedFiltersConditionCombineMode';
import { AdvancedFiltersConditionRow } from './components/AdvancedFiltersConditionRow';
import {
  type AdvancedFilterCondition,
  type AdvancedFiltersConfig,
} from './types/config.types';
import {
  type AdvancedFiltersPublicState,
  type InferAdvancedFilterPublicState,
} from './types/state.types';
import {
  advancedFilterConditionIsComplete,
  initialiseAdvancedFiltersReducer,
} from './utilities/reducer-utils';

export interface AdvancedFiltersContextProviderRef<
  U extends AdvancedFilterCondition = AdvancedFilterCondition,
> {
  /**
   * Call this method to flush new advanced filters into the context state
   * without remounting the component.
   */
  flushNewAdvancedFilters: (
    advancedFilters: AdvancedFiltersPublicState<U>,
  ) => void;
  /**
   * Call this method to remove all incomplete conditions from the context
   * state without remounting the component.
   */
  removeIncompleteRows: () => void;
  /**
   * Call this method to remove a specific condition by its ID.
   */
  removeConditionById: (id: string) => void;
  /**
   * Call this method to reset to the default empty advanced filters state.
   */
  reset: () => void;
}

interface AdvancedFiltersContextProps<
  Config extends AdvancedFiltersConfig = AdvancedFiltersConfig,
> extends PropsWithChildren {
  advancedFiltersConfig: Config;
  defaultAdvancedFilters?: InferAdvancedFilterPublicState<Config>;
  onAdvancedFiltersChange?: (
    newAdvancedFilters: InferAdvancedFilterPublicState<Config>,
  ) => void;
}

const _AdvancedFiltersContextProvider = forwardRef<
  AdvancedFiltersContextProviderRef,
  AdvancedFiltersContextProps
>(
  (
    {
      advancedFiltersConfig,
      defaultAdvancedFilters,
      onAdvancedFiltersChange,
      children,
    },
    ref,
  ) => {
    const prevCompletedState = useRef<string | null>(null);
    const [state, dispatch] = useReducer(
      advancedFiltersReducer,
      defaultAdvancedFilters,
      initialiseAdvancedFiltersReducer,
    );

    // expose imperative methods to parent component
    useImperativeHandle(ref, () => ({
      flushNewAdvancedFilters: (newAdvancedFilters) => {
        dispatch({
          type: 'FLUSH_NEW_ADVANCED_FILTERS',
          payload: newAdvancedFilters,
        });
      },
      removeIncompleteRows: () => {
        dispatch({ type: 'REMOVE_INCOMPLETE_ROWS' });
      },
      removeConditionById: (id: string) => {
        dispatch({ type: 'REMOVE_CONDITION', payload: { rowId: id } });
      },
      reset: () => {
        dispatch({ type: 'RESET' });
      },
    }));

    useEffect(() => {
      const conditionsWithIds = Object.entries(state.conditions)
        .filter((entry): entry is [string, AdvancedFilterCondition] =>
          advancedFilterConditionIsComplete(entry[1]),
        )
        .map(([rowId, condition]) => ({
          ...condition,
          id: rowId,
        }));

      const completedState = {
        conditionCombineMode: state.conditionCombineMode,
        conditions: conditionsWithIds,
      };
      const completedStateString = JSON.stringify(completedState);

      if (
        prevCompletedState.current &&
        prevCompletedState.current !== completedStateString
      ) {
        onAdvancedFiltersChange?.(completedState);
      }

      prevCompletedState.current = completedStateString;
    }, [state, onAdvancedFiltersChange]);

    return (
      <AdvancedFiltersContext.Provider
        value={{ state, dispatch, config: advancedFiltersConfig }}
      >
        {children}
      </AdvancedFiltersContext.Provider>
    );
  },
);

export const AdvancedFiltersContextProvider =
  _AdvancedFiltersContextProvider as <Config extends AdvancedFiltersConfig>(
    props: PropsWithRef<AdvancedFiltersContextProps<Config>> &
      RefAttributes<AdvancedFiltersContextProviderRef>,
  ) => ReactNode;

type AdvancedFiltersContentProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
>;

export const AdvancedFiltersContent = forwardRef<
  HTMLDivElement,
  AdvancedFiltersContentProps
>(({ className, ...props }, ref) => {
  const { common } = useAutoneTranslation();
  const { state, dispatch } = useAdvancedFiltersContext();
  const { conditions } = state;

  return (
    <div className={cn('flex flex-col gap-4', className)} {...props} ref={ref}>
      <AdvancedFiltersHeader />
      <div className="flex flex-col p-4 gap-3">
        <div className="flex flex-col gap-2">
          {Object.keys(conditions).map((rowId, idx) => (
            <div key={rowId} className="flex flex-row items-center w-full">
              <div className="basis-1/6">
                <AdvancedFiltersConditionCombineMode rowIdx={idx} />
              </div>
              <AdvancedFiltersConditionRow rowId={rowId} />
            </div>
          ))}
        </div>
        <Button
          id="add-condition-button"
          aria-label={common('advanced-filters.add-row')}
          variant={'ghost'}
          className="w-fit"
          onClick={() => dispatch({ type: 'ADD_CONDITION' })}
        >
          <PlusIcon />
          {common('advanced-filters.add-row')}
        </Button>
      </div>
    </div>
  );
});

export const AdvancedFiltersClearAllButton = forwardRef<
  HTMLButtonElement,
  ButtonProps
>(({ onClick, ...props }, ref) => {
  const { dispatch } = useAdvancedFiltersContext();

  const handleOnClick = (e: MouseEvent<HTMLButtonElement>) => {
    dispatch({ type: 'RESET' });
    onClick?.(e);
  };

  return (
    <Button onClick={handleOnClick} variant={'outline'} {...props} ref={ref} />
  );
});

// --------------------
// Accordion Advanced Filters Header
// --------------------
export const AdvancedFiltersHeader = () => {
  const { common } = useAutoneTranslation();

  return (
    <div className="flex items-center justify-between">
      <p className="text-lg font-medium">
        {common('advanced-filters.advanced-filters-ui-next-header')}
      </p>
      <ClearAllButton id="advanced-filters-clear-all" />
    </div>
  );
};
